import { SEGMENT_LENGTH, LANE_X, MIN_REACTION_S, SAFE_START_SEGMENTS, BASE_MAX_SPEED } from './constants';
import { hopAirSeconds, rampAirSeconds, TOP_SPEED_RATIO, worldGravity } from './physics';
import { Rng } from './rng';
import { getTheme, type PropKind, type Theme } from './themes';
import type { GameMode, ThemeId } from './types';

export type EntityKind =
  | 'rock'
  | 'log'
  | 'gate'
  | 'pit'
  | 'traffic'
  | 'geyser'
  | 'ramp'
  | 'boost'
  | 'orb'
  | 'gear';

export interface Entity {
  kind: EntityKind;
  seg: number;
  /** Normalised road-x of the centre. */
  x: number;
  /** Normalised half-width. */
  hw: number;
  /** World height you must be above to clear this. 0 means it must be dodged laterally. */
  clearY: number;
  /** Maximum height you may be at and still pass. Infinity for everything but gates. */
  ceilY: number;
  /** Lateral drift for traffic, normalised units/second. */
  vx: number;
  xMin: number;
  xMax: number;
  /** Geyser cycle in seconds. 0 means always dangerous. */
  period: number;
  /** Fraction of the cycle the geyser is erupting. */
  duty: number;
  phase: number;
  /** Stable per-entity randomness for rendering. */
  rnd: number;
  /** Set once collected so a pickup cannot double-score. */
  taken: boolean;
}

export interface PropInstance {
  kind: PropKind;
  x: number;
  scale: number;
  color: string;
  rnd: number;
}

export interface Segment {
  index: number;
  z: number;
  curve: number;
  /** Elevation at the start of the segment. Downhill, so this trends negative. */
  y: number;
  yEnd: number;
  /**
   * Ground height at the shoulder, world units, relative to the trail. Positive
   * is a hillside above you, negative is the ground falling away. Together the
   * two sides make a valley, a spine, or a bench cut into the slope.
   */
  bankL: number;
  bankR: number;
  /** Surface grip multiplier: below 1 is ice or wet tarmac. */
  grip: number;
  /** Part of a staircase: steeper, rattly, and much better jumped than ridden. */
  stairs: boolean;
  entities: Entity[];
  props: PropInstance[];
  finish: boolean;
}

export interface TrackOptions {
  seed: string;
  theme: ThemeId;
  mode: GameMode;
  /** Segment count for finite tracks. Omit or 0 for endless. */
  length?: number;
  /** Multiplies the difficulty ramp. Daily challenges use this. */
  difficultyMul?: number;
}

const CHUNK = 240;

/** Seconds between obstacle rows, from calm to frantic to endless-mode cruel. */
const ROW_GAP_S_EASY = 2.05;
const ROW_GAP_S_HARD = 0.95;
const ROW_GAP_S_EXTREME = 0.64;

/** Intensity at which spacing bottoms out. Only endless ever gets here. */
const MAX_INTENSITY = 2.1;

export type RowKind =
  | 'single'
  | 'double'
  | 'slalom'
  | 'pit'
  | 'gate'
  | 'traffic'
  | 'geyser'
  | 'ramp'
  | 'boost';

interface Feature {
  kind: 'straight' | 'left' | 'right';
  strength: number;
  segments: number;
}

export class Track {
  readonly seed: string;
  readonly theme: Theme;
  readonly mode: GameMode;
  /** Total segments for finite tracks, Infinity for endless. */
  readonly length: number;
  readonly difficultyMul: number;

  private segments: Segment[] = [];
  private pending = new Map<number, Entity[]>();
  private generatedTo = 0;

  private rng: Rng;
  private curve = 0;
  private targetCurve = 0;
  private featureLeft = 0;
  private grade = -0.085;
  private targetGrade = -0.085;
  private gradeLeft = 0;
  private y = 0;
  private bankL = 0;
  private bankR = 0;
  private bankTargetL = 0;
  private bankTargetR = 0;
  private bankLeft = 0;
  private stairSegs = 0;

  /** The upcoming obstacle row, and the lane the generator guarantees is clear. */
  private rowSeg = SAFE_START_SEGMENTS;
  /** Where that row was originally due, so crest deferral cannot run forever. */
  private rowFirstDue = SAFE_START_SEGMENTS;
  private rowLane = 1;
  private lastRowSeg = 0;
  private lastRowLane = 1;
  private lastRowKind: RowKind | null = null;
  private lastAirSeg = -9999;
  private lastLogSeg = -9999;
  /** No gate or gap before this segment: a rider could still be off the ground. */
  private airClearUntil = -9999;
  private gearCooldownSegs = 0;
  private boostCooldownSegs = 0;

  constructor(opts: TrackOptions) {
    this.seed = opts.seed;
    this.theme = getTheme(opts.theme);
    this.mode = opts.mode;
    this.difficultyMul = opts.difficultyMul ?? 1;
    this.length = opts.length && opts.length > 0 ? opts.length : Infinity;
    this.rng = new Rng(`${opts.seed}|${opts.theme}|${opts.mode}|v1`);
    this.rowLane = this.rng.int(0, 2);
    this.lastRowLane = this.rowLane;
    this.ensure(CHUNK * 2);
  }

  get finite(): boolean {
    return Number.isFinite(this.length);
  }

  get worldLength(): number {
    return this.finite ? this.length * SEGMENT_LENGTH : Infinity;
  }

  ensure(upToIndex: number): void {
    const cap = this.finite ? this.length : Number.MAX_SAFE_INTEGER;
    const target = Math.min(cap, upToIndex + CHUNK);
    while (this.generatedTo < target) {
      this.generateSegment(this.generatedTo);
      this.generatedTo++;
    }
  }

  segment(index: number): Segment {
    let i = index;
    if (i < 0) i = 0;
    if (this.finite && i >= this.length) i = this.length - 1;
    if (i >= this.generatedTo) this.ensure(i);
    return this.segments[i]!;
  }

  segmentAtZ(z: number): Segment {
    return this.segment(Math.floor(z / SEGMENT_LENGTH));
  }

  elevationAt(z: number): number {
    const seg = this.segmentAtZ(z);
    const t = clamp((z - seg.z) / SEGMENT_LENGTH, 0, 1);
    return seg.y + (seg.yEnd - seg.y) * t;
  }

  /** Downhill grade at a world-z; positive means descending. */
  gradeAt(z: number): number {
    const seg = this.segmentAtZ(z);
    return -(seg.yEnd - seg.y) / SEGMENT_LENGTH;
  }

  /** Release entities well behind the player so endless runs stay flat on memory. */
  prune(behindIndex: number): void {
    const cutoff = behindIndex - CHUNK * 2;
    if (cutoff <= 1) return;
    for (let i = Math.max(0, cutoff - CHUNK); i < cutoff; i++) {
      const s = this.segments[i];
      if (s && (s.entities.length || s.props.length)) {
        s.entities = [];
        s.props = [];
      }
    }
  }

  /**
   * 0 at the start gate, 1 at the campaign's peak. Finite tracks ramp across
   * their whole length and stop. Endless keeps climbing forever, slowly, so a
   * run always ends eventually no matter how good you are.
   */
  intensityAt(index: number): number {
    const raw = Math.max(0, index - SAFE_START_SEGMENTS);
    if (this.finite) {
      const base = clamp(raw / Math.max(1, this.length - SAFE_START_SEGMENTS), 0, 1);
      return clamp(easeInOut(base) * this.theme.difficulty * this.difficultyMul, 0, MAX_INTENSITY);
    }
    const base = 1 - Math.exp(-raw / 2600);
    const grind = raw / 15000;
    const themeMul = Math.max(0.95, this.theme.difficulty) * this.difficultyMul;
    return clamp((base + grind) * themeMul, 0, MAX_INTENSITY);
  }

  /** Speed we expect a competent rider to be carrying here. Drives spacing. */
  nominalSpeed(intensity: number): number {
    return BASE_MAX_SPEED * this.theme.speedMul * (0.7 + 0.3 * clamp(intensity, 0, 1));
  }

  /** Fastest a rider can possibly be going here: tucked, mid-send, on the clamp. */
  get topSpeed(): number {
    return BASE_MAX_SPEED * this.theme.speedMul * TOP_SPEED_RATIO;
  }

  /** Segments of track covered by the longest ramp flight this world allows. */
  get rampClearSegs(): number {
    const g = worldGravity(this.theme.gravity);
    const seconds = rampAirSeconds(g, TOP_SPEED_RATIO);
    return Math.ceil((seconds * this.topSpeed) / SEGMENT_LENGTH) + 8;
  }

  /** Segments covered by the longest hop a rider can choose to make. */
  get hopClearSegs(): number {
    const g = worldGravity(this.theme.gravity);
    return Math.ceil((hopAirSeconds(g) * this.topSpeed) / SEGMENT_LENGTH) + 6;
  }

  // --- generation -------------------------------------------------------

  private generateSegment(index: number): void {
    const rng = this.rng;

    if (this.featureLeft <= 0) {
      const f = this.rollFeature(rng);
      this.featureLeft = f.segments;
      this.targetCurve = f.kind === 'straight' ? 0 : f.strength * (f.kind === 'left' ? -1 : 1);
    }
    this.featureLeft--;
    this.curve += (this.targetCurve - this.curve) * 0.055;

    // Staircases are their own terrain feature: steep, and they own the grade.
    let onStairs = false;
    if (this.stairSegs > 0) {
      this.stairSegs--;
      onStairs = true;
    } else if (
      this.theme.stairs &&
      index > SAFE_START_SEGMENTS &&
      index < (this.finite ? this.length - 220 : Infinity) &&
      rng.chance(0.0045)
    ) {
      this.stairSegs = rng.int(16, 38);
      onStairs = true;
    }

    if (onStairs) {
      this.targetGrade = -0.2;
      this.gradeLeft = Math.max(this.gradeLeft, 1);
    } else if (this.gradeLeft <= 0) {
      // Wide swing and short features: proper crests and dips rather than a
      // gently sloping road. The mean still descends hard.
      this.targetGrade = -0.09 + rng.range(-0.22, 0.16) * this.theme.hilliness;
      this.gradeLeft = rng.int(22, 70);
    }
    this.gradeLeft--;
    this.grade += (this.targetGrade - this.grade) * (onStairs ? 0.12 : 0.075);
    const yStart = this.y;
    const yEnd = yStart + this.grade * SEGMENT_LENGTH;
    this.y = yEnd;

    if (this.bankLeft <= 0) {
      const [l, r] = this.rollTerrain(rng);
      this.bankTargetL = l;
      this.bankTargetR = r;
      this.bankLeft = rng.int(70, 220);
    }
    this.bankLeft--;
    this.bankL += (this.bankTargetL - this.bankL) * 0.05;
    this.bankR += (this.bankTargetR - this.bankR) * 0.05;

    const seg: Segment = {
      index,
      z: index * SEGMENT_LENGTH,
      curve: this.curve,
      y: yStart,
      yEnd,
      bankL: this.bankL,
      bankR: this.bankR,
      grip: 1,
      stairs: onStairs,
      entities: [],
      props: [],
      finish: this.finite && index === this.length - 1,
    };

    const queued = this.pending.get(index);
    if (queued) {
      seg.entities.push(...queued);
      this.pending.delete(index);
    }

    if (this.theme.grip < 1 && index > SAFE_START_SEGMENTS && rng.chance(0.014)) {
      seg.grip = this.theme.grip;
    }

    this.placeProps(seg, rng);

    if (this.gearCooldownSegs > 0) this.gearCooldownSegs--;
    if (this.boostCooldownSegs > 0) this.boostCooldownSegs--;

    const noRowsAfter = this.finite ? this.length - 150 : Infinity;
    if (index >= this.rowSeg && index < noRowsAfter) {
      // Never hide a row just over a crest: on rising ground you cannot see it
      // until you are on top of it. Slide it down the far side instead.
      if (this.grade > -0.012 && index - this.rowFirstDue < 160) {
        this.rowSeg = index + 6;
      } else {
        this.placeRow(seg, index, rng);
      }
    }

    this.layOrbs(seg, index, rng);
    this.segments[index] = seg;
  }

  private rollFeature(rng: Rng): Feature {
    const c = this.theme.curviness;
    const kind = rng.weighted<Feature['kind']>([
      ['straight', 1.5],
      ['left', 1.7 * c],
      ['right', 1.7 * c],
    ]);
    if (kind === 'straight') return { kind, strength: 0, segments: rng.int(30, 90) };
    return { kind, strength: rng.range(1.2, 4.8) * c, segments: rng.int(45, 165) };
  }

  /** Shoulder heights for the next stretch: a valley, a spine, or a bench cut. */
  private rollTerrain(rng: Rng): [number, number] {
    const t = this.theme.terrain;
    const rise = () => rng.range(1100, 3200);
    const drop = () => -rng.range(900, 2600);
    const kind = rng.weighted<'flat' | 'valley' | 'spine' | 'cutL' | 'cutR'>([
      ['flat', 1.3],
      ['valley', t.valley],
      ['spine', t.spine],
      ['cutL', t.cut],
      ['cutR', t.cut],
    ]);
    switch (kind) {
      case 'valley':
        return [rise(), rise()];
      case 'spine':
        return [drop(), drop()];
      case 'cutL':
        return [rise(), drop()];
      case 'cutR':
        return [drop(), rise()];
      default:
        return [rng.range(-300, 400), rng.range(-300, 400)];
    }
  }

  private placeProps(seg: Segment, rng: Rng): void {
    if (!rng.chance(0.26)) return;
    const spec = rng.weighted(this.theme.props.map((p) => [p, p.weight] as const));
    const side = rng.chance(0.5) ? -1 : 1;
    seg.props.push({
      kind: spec.kind,
      x: side * rng.range(spec.minX, spec.maxX),
      scale: spec.scale * rng.range(0.75, 1.3),
      color: rng.pick(spec.colors),
      rnd: rng.next(),
    });
    if (rng.chance(0.3)) {
      seg.props.push({
        kind: spec.kind,
        x: -side * rng.range(spec.minX, spec.maxX),
        scale: spec.scale * rng.range(0.75, 1.3),
        color: rng.pick(spec.colors),
        rnd: rng.next(),
      });
    }
  }

  /** Queue an entity into a segment that may not exist yet. */
  private emit(index: number, e: Entity): void {
    if (this.finite && index >= this.length) return;
    const existing = this.segments[index];
    if (existing && index < this.generatedTo) {
      existing.entities.push(e);
      return;
    }
    const list = this.pending.get(index);
    if (list) list.push(e);
    else this.pending.set(index, [e]);
  }

  /** Pick where the next row lands and which lane it will leave open. */
  private schedule(fromIndex: number, rng: Rng, minSegs = 0): void {
    const intensity = clamp(this.intensityAt(fromIndex), 0, MAX_INTENSITY);
    const segsPerSecond = this.nominalSpeed(intensity) / SEGMENT_LENGTH;
    const base =
      intensity <= 1
        ? lerp(ROW_GAP_S_EASY, ROW_GAP_S_HARD, intensity)
        : lerp(ROW_GAP_S_HARD, ROW_GAP_S_EXTREME, (intensity - 1) / (MAX_INTENSITY - 1));
    const seconds = Math.max(MIN_REACTION_S, base * rng.range(0.86, 1.2));
    const segs = Math.max(12, Math.round(seconds * segsPerSecond), minSegs);

    this.lastRowSeg = fromIndex;
    this.lastRowLane = this.rowLane;
    this.rowSeg = fromIndex + segs;
    this.rowFirstDue = this.rowSeg;

    // Bias toward adjacent lanes on low-grip worlds so the required line stays makeable.
    const cur = this.rowLane;
    const farWeight = this.theme.grip < 0.8 ? 0.5 : 1.5;
    const options: (readonly [number, number])[] = [0, 1, 2].map((l) => {
      const d = Math.abs(l - cur);
      return [l, d === 0 ? 1.1 : d === 1 ? 3.4 : farWeight] as const;
    });
    this.rowLane = rng.weighted(options);
  }

  private placeRow(seg: Segment, index: number, rng: Rng): void {
    const w = this.theme.hazardWeights;
    const intensity = this.intensityAt(index);
    const i = clamp(intensity, 0, 1);
    const segsPerSecond = this.nominalSpeed(intensity) / SEGMENT_LENGTH;
    const open = this.rowLane;
    // Gates, gaps, traffic and fire columns cannot be flown over, so none of
    // them may land while a rider is still committed to the air. Boulders can be
    // steered around mid-flight, so they stay available and keep the trail busy.
    const grounded = index > this.airClearUntil;
    const gateAllowed = grounded && index - this.lastAirSeg > this.hopClearSegs * 0.5;
    const pitAllowed = grounded && index - this.lastLogSeg > this.hopClearSegs;
    const repeat = (k: RowKind) => (this.lastRowKind === k ? 0.35 : 1);

    const kind = rng.weighted<RowKind>([
      ['single', (3 + (1 - i) * 3) * repeat('single')],
      ['double', (1.2 + i * 2.6) * repeat('double')],
      ['slalom', (0.4 + i * 2.0) * repeat('slalom')],
      ['pit', pitAllowed ? (w.pit ?? 1) * (0.4 + i * 1.3) * repeat('pit') : 0],
      ['gate', gateAllowed ? (w.gate ?? 1) * (0.4 + i * 1.3) * repeat('gate') : 0],
      ['traffic', grounded ? (w.traffic ?? 0) * (0.4 + i * 1.6) * repeat('traffic') : 0],
      ['geyser', grounded ? (w.geyser ?? 0) * (0.4 + i * 1.6) * repeat('geyser') : 0],
      ['ramp', (w.ramp ?? 1) * 2 * repeat('ramp')],
      ['boost', this.boostCooldownSegs > 0 ? 0 : 1.1],
    ]);

    this.lastRowKind = kind;
    const blockedLanes = [0, 1, 2].filter((l) => l !== open);

    switch (kind) {
      case 'ramp': {
        seg.entities.push(makeEntity('ramp', index, LANE_X[open]!, 0.26, rng));
        this.lastAirSeg = index;
        const airSegs = this.rampClearSegs;
        this.airClearUntil = index + airSegs;
        this.maybeAirGear(index + Math.round(airSegs * 0.42), LANE_X[open]!, rng);
        // Rows keep their normal cadence through the flight; the air-clear window
        // above is what keeps the unflyable hazards out of the landing.
        this.schedule(index, rng);
        break;
      }
      case 'boost': {
        seg.entities.push(makeEntity('boost', index, LANE_X[open]!, 0.28, rng));
        this.boostCooldownSegs = Math.round(6 * segsPerSecond);
        this.schedule(index, rng);
        break;
      }
      case 'single': {
        const blocked = rng.pick(blockedLanes);
        seg.entities.push(this.groundHazard(index, blocked, rng, i));
        this.schedule(index, rng);
        break;
      }
      case 'double': {
        for (const l of blockedLanes) seg.entities.push(this.groundHazard(index, l, rng, i));
        this.schedule(index, rng);
        break;
      }
      case 'slalom': {
        // Three staggered singles that force a weave and end on the open lane.
        const step = Math.max(8, Math.round(0.46 * segsPerSecond));
        const seq = slalomSequence(open, rng.chance(0.5) ? 1 : -1);
        for (let k = 0; k < seq.length; k++) {
          const at = index + k * step;
          this.emit(at, this.groundHazard(at, seq[k]!, rng, i));
        }
        this.schedule(index, rng, step * seq.length + Math.round(0.5 * segsPerSecond));
        break;
      }
      case 'pit': {
        const len = Math.max(3, Math.round(rng.range(0.15, 0.28) * segsPerSecond));
        for (let k = 0; k < len; k++) {
          this.emit(index + k, makeEntity('pit', index + k, 0, 1.1, rng, { clearY: 1 }));
        }
        this.lastAirSeg = index + len;
        this.airClearUntil = index + len + this.hopClearSegs;
        this.schedule(index, rng, len + Math.round(0.5 * segsPerSecond));
        break;
      }
      case 'gate': {
        for (const l of blockedLanes) {
          seg.entities.push(makeEntity('gate', index, LANE_X[l]!, 0.31, rng, { clearY: 0, ceilY: 100 }));
        }
        this.schedule(index, rng);
        break;
      }
      case 'traffic': {
        const lane = rng.pick(blockedLanes);
        const dir = lane === 0 ? 1 : lane === 2 ? -1 : rng.chance(0.5) ? 1 : -1;
        // Patrols only on its own side of the open lane, so the open lane stays open.
        const edge = LANE_X[open]! + (open < lane ? 0.34 : -0.34);
        const outer = lane < open ? -0.95 : 0.95;
        seg.entities.push(
          makeEntity('traffic', index, LANE_X[lane]!, 0.25, rng, {
            vx: dir * rng.range(0.16, 0.42),
            xMin: Math.min(edge, outer),
            xMax: Math.max(edge, outer),
          }),
        );
        this.schedule(index, rng);
        break;
      }
      case 'geyser': {
        const chosen = rng.chance(0.4 + i * 0.3) ? blockedLanes : [rng.pick(blockedLanes)];
        for (const l of chosen) {
          seg.entities.push(
            makeEntity('geyser', index, LANE_X[l]!, 0.28, rng, {
              period: rng.range(1.9, 3.1),
              duty: 0.42,
              phase: rng.next(),
            }),
          );
        }
        this.schedule(index, rng);
        break;
      }
    }
  }

  private groundHazard(index: number, lane: number, rng: Rng, i: number): Entity {
    const w = this.theme.hazardWeights;
    const kind = rng.weighted<'rock' | 'log'>([
      ['rock', w.rock ?? 2],
      ['log', (w.log ?? 2) * (1.4 - i * 0.4)],
    ]);
    if (kind === 'log') {
      this.lastLogSeg = Math.max(this.lastLogSeg, index);
      this.airClearUntil = Math.max(this.airClearUntil, index + this.hopClearSegs);
      return makeEntity('log', index, LANE_X[lane]!, 0.31, rng, { clearY: 150 });
    }
    // Dodge only, deliberately. Two obstacle families with one rule each beats a
    // boulder you can *sometimes* clear: the player must never have to guess.
    return makeEntity('rock', index, LANE_X[lane]!, rng.range(0.2, 0.3), rng);
  }

  private maybeAirGear(index: number, x: number, rng: Rng): void {
    if (this.gearCooldownSegs > 0) return;
    if (!rng.chance(0.5)) return;
    this.emit(index, makeEntity('gear', index, x, 0.26, rng, { clearY: 240, ceilY: 1400 }));
    this.gearCooldownSegs = 1400;
  }

  /**
   * Orbs trace the line from the last row's open lane to the next one's, so
   * chasing the sparkle is always a survivable line. Reward, not tax.
   */
  private layOrbs(seg: Segment, index: number, rng: Rng): void {
    if (index % 5 !== 0 || index < 15) return;
    if (seg.entities.length > 0) return;
    const span = Math.max(1, this.rowSeg - this.lastRowSeg);
    const t = clamp((index - this.lastRowSeg) / span, 0, 1);
    const from = LANE_X[this.lastRowLane] ?? 0;
    const to = LANE_X[this.rowLane] ?? 0;
    const x = lerp(from, to, easeInOut(t));
    if (!rng.chance(0.7)) return;
    seg.entities.push(makeEntity('orb', index, x, 0.17, rng, { clearY: 0, ceilY: Infinity }));
  }
}

/** A left-right-left (or mirrored) weave whose final gap is the open lane. */
function slalomSequence(open: number, dir: number): number[] {
  const other = [0, 1, 2].filter((l) => l !== open);
  const first = dir > 0 ? other[0]! : other[other.length - 1]!;
  const second = dir > 0 ? other[other.length - 1]! : other[0]!;
  return [first, second, first];
}

interface EntityOverrides {
  clearY?: number;
  ceilY?: number;
  vx?: number;
  xMin?: number;
  xMax?: number;
  period?: number;
  duty?: number;
  phase?: number;
}

function makeEntity(
  kind: EntityKind,
  seg: number,
  x: number,
  hw: number,
  rng: Rng,
  o: EntityOverrides = {},
): Entity {
  return {
    kind,
    seg,
    x,
    hw,
    clearY: o.clearY ?? 0,
    ceilY: o.ceilY ?? Infinity,
    vx: o.vx ?? 0,
    xMin: o.xMin ?? -1,
    xMax: o.xMax ?? 1,
    period: o.period ?? 0,
    duty: o.duty ?? 0,
    phase: o.phase ?? 0,
    rnd: rng.next(),
    taken: false,
  };
}

export function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
}

/** Is this geyser erupting right now? */
export function geyserActive(e: Entity, timeS: number): boolean {
  if (e.period <= 0) return true;
  const p = (((timeS / e.period + e.phase) % 1) + 1) % 1;
  return p < e.duty;
}

/** Current lateral position of a patrolling entity, ping-ponging between bounds. */
export function entityX(e: Entity, timeS: number): number {
  if (e.vx === 0) return e.x;
  const span = e.xMax - e.xMin;
  if (span <= 0) return e.x;
  const travel = Math.abs(e.vx) * timeS + (e.vx > 0 ? e.x - e.xMin : e.xMax - e.x);
  const period = span * 2;
  let p = ((travel % period) + period) % period;
  if (p > span) p = period - p;
  return e.vx > 0 ? e.xMin + p : e.xMax - p;
}
